import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CalendarDays, Trash2, Bell, Repeat, List, LayoutGrid, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  RECURRENCE_OPTIONS,
  RECURRENCE_LABEL,
  dateKey,
  occursOn,
  expandOccurrences,
} from "@/lib/recurrence";
import RingtonePicker from "@/components/RingtonePicker";
import { DEFAULT_SOUND, stopSound } from "@/lib/reminderSounds";

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function MiniMonth({ date, dotsByDay, onOpen }) {
  const first = startOfMonth(date);
  const startDow = first.getDay();
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const today = new Date();
  const isToday = (d) => d === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  return (
    <button
      onClick={onOpen}
      data-testid="calendar-mini-open"
      className="w-full text-left bg-[#121212] border border-[#1E1E1E] rounded-xl p-2 hover:border-[#27272A] transition-all"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#9CA3AF] font-medium">
          <CalendarDays className="h-3 w-3 text-[#3B82F6]" />
          {date.toLocaleString(undefined, { month: "short", year: "numeric" })}
        </div>
        <span className="text-[9px] text-[#6B7280]">Open</span>
      </div>
      <div className="grid grid-cols-7 gap-[1px] text-center text-[8px] text-[#6B7280]">
        {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-[1px]">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="h-4" />;
          const k = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const dot = dotsByDay[k];
          return (
            <div key={i} className={`h-4 text-[9px] rounded flex items-center justify-center relative ${isToday(d) ? "bg-[#2563EB] text-white" : "text-[#D1D5DB]"}`}>
              {d}
              {dot && !isToday(d) && (
                <span className="absolute -bottom-0 flex gap-[1px]">
                  {dot.timed > 0 && <span className="h-[3px] w-[3px] rounded-full bg-[#3B82F6]" />}
                  {dot.recurring > 0 && <span className="h-[3px] w-[3px] rounded-full bg-[#F59E0B]" />}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </button>
  );
}

export default function CalendarWidget({ onJumpToItem }) {
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [selected, setSelected] = useState(new Date());
  // "calendar" shows the grid + day column (default).
  // "list"     shows a flat, searchable list of every reminder — fastest way
  //            to clean up stale ones in bulk.
  const [mode, setMode] = useState("calendar");
  const [listQuery, setListQuery] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const r = await api.get("/reminders"); setReminders(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  // Refresh when a reminder is created elsewhere (e.g. from a message menu
  // via ReminderPickerDialog).
  useEffect(() => {
    const onCreated = () => { load(); };
    window.addEventListener("notelosh:reminder-created", onCreated);
    window.addEventListener("notelosh:synced", onCreated);
    return () => {
      window.removeEventListener("notelosh:reminder-created", onCreated);
      window.removeEventListener("notelosh:synced", onCreated);
    };
  }, []);

  // Compute {timed, recurring} dot counts for the current mini-month.
  const dotsByDay = useMemo(() => {
    const m = {};
    const now = new Date();
    const first = startOfMonth(now);
    const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    for (let day = 1; day <= days; day++) {
      const k = dateKey(new Date(first.getFullYear(), first.getMonth(), day));
      for (const r of reminders) {
        if (!occursOn(r, k)) continue;
        if (!m[k]) m[k] = { timed: 0, recurring: 0 };
        if ((r.recurrence || "none") === "none") m[k].timed += 1;
        else m[k].recurring += 1;
      }
    }
    return m;
  }, [reminders]);

  // For the full-dialog Calendar: dot arrays for a ±1 year window.
  const dialogDots = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const to = new Date(now.getFullYear() + 1, now.getMonth() + 1, 0);
    const occ = expandOccurrences(reminders, from, to);
    const timed = new Set();
    const recurring = new Set();
    for (const o of occ) {
      (o.is_recurring ? recurring : timed).add(o.occurrence_date);
    }
    return { timed, recurring };
  }, [reminders]);

  // All reminders grouped by date for the flat "View list" mode.
  // Upcoming first (ascending), then past (ascending so oldest at the bottom).
  const groupedReminders = useMemo(() => {
    const todayKey = dateKey(new Date());
    const q = listQuery.trim().toLowerCase();
    const filtered = reminders.filter((r) => {
      if (!q) return true;
      return (r.text || "").toLowerCase().includes(q);
    });
    const byDate = new Map();
    for (const r of filtered) {
      const k = r.date || "undated";
      if (!byDate.has(k)) byDate.set(k, []);
      byDate.get(k).push(r);
    }
    const keys = Array.from(byDate.keys()).sort();
    const upcoming = keys.filter((k) => k >= todayKey);
    const past = keys.filter((k) => k < todayKey);
    return { upcoming, past, byDate, total: filtered.length };
  }, [reminders, listQuery]);

  const prettyDate = (k) => {
    if (!k || k === "undated") return "No date";
    const [y, m, d] = k.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  // All occurrences (one-time + recurring) that land on the selected day.
  const dayReminders = useMemo(() => {
    const k = dateKey(selected);
    return reminders
      .filter((r) => occursOn(r, k))
      .map((r) => ({ ...r, occurrence_date: k, is_recurring: (r.recurrence || "none") !== "none" }));
  }, [reminders, selected]);

  // Shared row renderer for both calendar + list views.
  const renderRow = (r, { showDate = false } = {}) => {
    const isRecurring = r.is_recurring ?? ((r.recurrence || "none") !== "none");
    const canJump = !!(r.title_id && onJumpToItem);
    const jump = () => {
      if (!canJump) return;
      onJumpToItem({ title_id: r.title_id, item_id: r.item_id });
      setOpen(false);
    };
    return (
      <li
        key={r.reminder_id}
        data-testid={`reminder-${r.reminder_id}`}
        onClick={jump}
        role={canJump ? "button" : undefined}
        tabIndex={canJump ? 0 : undefined}
        onKeyDown={(e) => { if (canJump && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); jump(); } }}
        title={canJump ? "Open this note" : (r.item_id ? "Original note was deleted" : "Freeform reminder — no linked note")}
        className={`flex items-start gap-2 p-3 rounded-xl bg-[#1E1E1E] border border-[#27272A] transition-all ${canJump ? "cursor-pointer hover:border-[#3B82F6]/60 hover:bg-[#27272A]" : "opacity-90"}`}
      >
        {isRecurring
          ? <Repeat className="h-4 w-4 mt-0.5 text-[#F59E0B] shrink-0" />
          : <Bell className="h-4 w-4 mt-0.5 text-[#3B82F6] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#F3F4F6]">{r.text}</div>
          <div className="text-[11px] text-[#6B7280] mt-0.5">
            {showDate ? `${prettyDate(r.date)} · ` : ""}{r.time}
            {isRecurring ? ` · ${RECURRENCE_LABEL[r.recurrence] || "Repeats"}` : ""}
            {canJump ? " · Tap to open note" : (r.item_id ? " · Note deleted" : "")}
          </div>
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            data-testid={`delete-reminder-${r.reminder_id}`}
            onClick={(e) => { e.stopPropagation(); del(r.reminder_id); }}
            title={isRecurring ? "Delete this recurring reminder (removes all occurrences)" : "Delete reminder"}
            className="h-7 w-7 text-[#9CA3AF] hover:text-[#FCA5A5] hover:bg-[#27272A]"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            data-testid={`edit-reminder-${r.reminder_id}`}
            onClick={(e) => { e.stopPropagation(); setEditing(reminders.find((x) => x.reminder_id === r.reminder_id) || r); }}
            title="Edit reminder"
            className="h-7 w-7 text-[#9CA3AF] hover:text-[#93C5FD] hover:bg-[#27272A]"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      </li>
    );
  };

  const del = async (id) => {
    // Optimistically drop it from the list — this endpoint is idempotent for
    // the user's mental model ("I clicked delete, it should go away"). If the
    // server 404s (e.g. reminder already removed on another device, or the
    // owning item was deleted), we still hide it locally instead of crashing
    // with an uncaught axios error banner.
    setReminders((prev) => prev.filter((r) => r.reminder_id !== id));
    try {
      await api.delete(`/reminders/${id}`);
      toast.success("Reminder removed");
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.success("Reminder removed");
      } else {
        toast.error("Could not remove reminder");
        // Roll back so the user can retry — refetch from the source of truth.
        try { const r = await api.get("/reminders"); setReminders(r.data); } catch { /* ignore */ }
      }
    }
  };

  return (
    <>
      <div className="px-3 pb-2 pt-1">
        <MiniMonth date={new Date()} dotsByDay={dotsByDay} onOpen={() => setOpen(true)} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="calendar-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#27272A]">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="font-heading flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-[#3B82F6]" /> Calendar &amp; reminders
              </DialogTitle>
              <div className="flex items-center gap-1 bg-[#1E1E1E] border border-[#27272A] rounded-full p-0.5" role="tablist" aria-label="Reminder view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "calendar"}
                  data-testid="calendar-view-tab"
                  onClick={() => setMode("calendar")}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-all ${mode === "calendar" ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-[#F3F4F6]"}`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" /> Calendar
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "list"}
                  data-testid="list-view-tab"
                  onClick={() => setMode("list")}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-all ${mode === "list" ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-[#F3F4F6]"}`}
                >
                  <List className="h-3.5 w-3.5" /> View list
                  {reminders.length > 0 && (
                    <span className={`ml-0.5 text-[10px] px-1.5 py-[1px] rounded-full ${mode === "list" ? "bg-white/20" : "bg-[#27272A] text-[#6B7280]"}`}>
                      {reminders.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </DialogHeader>
          {mode === "calendar" ? (
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-4 border-r border-[#27272A]">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={(d) => d && setSelected(d)}
                modifiers={{
                  timed: Array.from(dialogDots.timed).map((k) => new Date(k + "T00:00")),
                  recurring: Array.from(dialogDots.recurring).map((k) => new Date(k + "T00:00")),
                }}
                modifiersClassNames={{
                  timed:
                    "after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-[#3B82F6] relative",
                  recurring:
                    "before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:translate-x-[3px] before:w-1 before:h-1 before:rounded-full before:bg-[#F59E0B] relative",
                }}
                className="bg-transparent"
              />
              <div className="flex items-center gap-4 mt-3 px-1 text-[10px] text-[#9CA3AF]">
                <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" /> One-time</span>
                <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" /> Repeats</span>
              </div>
            </div>
            <div className="p-5 min-h-[260px]">
              <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">
                {selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
              {dayReminders.length === 0 ? (
                <div className="text-sm text-[#6B7280]">No reminders on this day. From any note, use the menu → &quot;Set as reminder&quot;.</div>
              ) : (
                <ul className="space-y-2">
                  {dayReminders.map((r) => renderRow(r))}
                </ul>
              )}
            </div>
          </div>
          ) : (
          <div className="p-5 max-h-[70vh] overflow-y-auto" data-testid="reminders-list-view">
            <div className="relative mb-4">
              <Search className="h-4 w-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                data-testid="reminders-list-search"
                placeholder="Search reminders…"
                className="w-full bg-[#1E1E1E] border border-[#27272A] rounded-xl pl-9 pr-3 py-2 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            {groupedReminders.total === 0 ? (
              <div className="text-sm text-[#6B7280] py-6 text-center">
                {listQuery ? "No reminders match that search." : "You haven't set any reminders yet. From any note, use the menu → \"Set as reminder\"."}
              </div>
            ) : (
              <div className="space-y-5">
                {groupedReminders.upcoming.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#3B82F6] font-semibold mb-2 px-1">Upcoming</div>
                    <div className="space-y-4">
                      {groupedReminders.upcoming.map((k) => (
                        <div key={k}>
                          <div className="text-xs text-[#9CA3AF] mb-1.5 px-1">{prettyDate(k)}</div>
                          <ul className="space-y-2">
                            {groupedReminders.byDate.get(k).map((r) => renderRow(r, { showDate: false }))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {groupedReminders.past.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2 px-1">Past</div>
                    <div className="space-y-4">
                      {groupedReminders.past.map((k) => (
                        <div key={k}>
                          <div className="text-xs text-[#6B7280] mb-1.5 px-1">{prettyDate(k)}</div>
                          <ul className="space-y-2">
                            {groupedReminders.byDate.get(k).map((r) => renderRow(r, { showDate: false }))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>
      <ReminderPickerDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        reminder={editing}
      />
    </>
  );
}

// Reminder picker dialog for messages (or editing an existing reminder)
export function ReminderPickerDialog({ open, onOpenChange, defaultText, itemId, reminder }) {
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("09:00");
  const [text, setText] = useState(defaultText || "");
  const [recurrence, setRecurrence] = useState("none");
  const [sound, setSound] = useState(DEFAULT_SOUND);
  const [library, setLibrary] = useState([]);

  const loadLibrary = useCallback(async () => {
    try {
      const r = await api.get("/reminder-sounds");
      setLibrary(r.data.sounds || []);
    } catch { /* library stays empty */ }
  }, []);

  useEffect(() => {
    if (open) {
      if (reminder) {
        const [y, m, d] = (reminder.date || dateKey(new Date())).split("-").map(Number);
        setText(reminder.text || "");
        setDate(new Date(y, m - 1, d));
        setTime(reminder.time || "09:00");
        setRecurrence(reminder.recurrence || "none");
        setSound(reminder.sound || DEFAULT_SOUND);
      } else {
        setText(defaultText || "");
        setDate(new Date());
        setTime("09:00");
        setRecurrence("none");
        setSound(DEFAULT_SOUND);
      }
      loadLibrary();
    } else {
      stopSound();
    }
  }, [open, defaultText, reminder, loadLibrary]);

  const save = async () => {
    const iso = dateKey(date);
    const body = { item_id: reminder ? reminder.item_id : (itemId || null), text, date: iso, time, recurrence, sound };
    try {
      if (reminder) await api.put(`/reminders/${reminder.reminder_id}`, body);
      else await api.post("/reminders", body);
      window.dispatchEvent(new CustomEvent("notelosh:reminder-created"));
      const suffix = recurrence === "none" ? "" : ` (${RECURRENCE_LABEL[recurrence]})`;
      toast.success(`Reminder ${reminder ? "updated" : "set"} for ${iso} at ${time}${suffix}`);
      onOpenChange(false);
    } catch {
      toast.error(reminder ? "Could not update reminder" : "Could not set reminder");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reminder-picker-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{reminder ? "Edit reminder" : "Set as reminder"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="reminder-text-input"
            className="w-full bg-[#1E1E1E] border border-[#27272A] rounded-xl px-4 py-3 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6]"
            rows={2}
            placeholder="Remind me to…"
          />
          <Calendar key={dateKey(date)} defaultMonth={date} mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="bg-[#1E1E1E] border border-[#27272A] rounded-xl" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF] shrink-0">Time</span>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="reminder-time-input"
              className="flex-1 bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-[#F3F4F6] [color-scheme:dark]"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF] shrink-0 flex items-center gap-1">
              <Repeat className="h-3.5 w-3.5" /> Repeat
            </span>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              data-testid="reminder-recurrence-select"
              className="flex-1 bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-[#F3F4F6] focus:outline-none focus:border-[#3B82F6] [color-scheme:dark]"
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <RingtonePicker
            value={sound}
            onChange={setSound}
            library={library}
            onLibraryChange={loadLibrary}
          />
          {recurrence !== "none" && (            <div className="flex items-start gap-2 text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/25 rounded-lg px-3 py-2">
              <Repeat className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This is a constant reminder — it will sit on your calendar {RECURRENCE_LABEL[recurrence].toLowerCase()} starting {dateKey(date)}. No bell alert.
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full text-[#9CA3AF] hover:bg-[#1E1E1E]">Cancel</Button>
            <Button onClick={save} data-testid="reminder-save-btn" disabled={!text.trim()} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">{reminder ? "Save changes" : "Set reminder"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
